// @flow
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { chunk, size } from 'lodash';
import classNames from 'classnames';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import {
  getTopAccordionProduct,
  selectHasFavorites,
  makeSelectGwpProducts,
} from 'selectors/productsSelectors';
import { selectIsCartEmpty } from 'selectors/cartSelectors';
import { selectSelectedCategoryName } from 'selectors/categoriesSelectors';
import { selectTeamCartProducts } from 'selectors/socialEcommSelectors';

import withWindowTopIntersection from 'HOC/withWindowTopIntersection/withWindowTopIntersection';
import withSelectedProductLink from 'HOC/withSelectedProductLink/withSelectedProductLink';
import withSelectedCategoryId from 'HOC/withSelectedCategoryId/withSelectedCategoryId';
import withDisableScrollRestoration from 'HOC/withDisableScrollRestoration/withDisableScrollRestoration';
import withOptimizelyFF, {
  FEATURED_UI_DECISIONS,
} from 'HOC/withOptimizely/withOptimizelyFF';
import type { WithWindowTopIntersectionInjectedProps } from 'HOC/withWindowTopIntersection/withWindowTopIntersection';
import type { WithSelectedProductLinkInjectedProps } from 'HOC/withSelectedProductLink/withSelectedProductLink';

import { VIEW_SIZE } from 'utils/device';
import { trackOptimizelyExperiment } from 'utils/segment';
import { isPaginatedSortBy } from 'utils/sort';
import { CATEGORY_ID, isStaticCategory } from 'utils/categories';

import type { State, BundledProduct, Sort } from 'sharedTypes';

import { ReactComponent as LoadingSVG } from 'images/loading.svg';

import ScrollToTopButton from 'shared-components/ScrollToTopButton/ScrollToTopButton';
import ErrorBoundary from 'shared-components/ErrorBoundary/ErrorBoundary';

import {
  requestProducts,
  requestProductsById,
  requestBestSellingProducts,
} from 'services/actions/productsActions';
import {
  resetSearch,
  updateShowSearchBar,
} from 'services/actions/searchActions';

import ProductGrid from '../ProductGrid/ProductGrid';
import ProductAccordion from '../ProductAccordion/ProductAccordion';
import SponsoredWarningPopup from '../SponsoredWarningPopup/SponsoredWarningPopup';
import SearchResults from '../../Headers/SearchResults/SearchResults';
import ShopAllButton from '../../Headers/SearchResults/ShopAllButton';
import GwpPopupsContainer from '../GwpPopups/GwpPopupsContainer';
import FeaturedProducts from '../FeaturedProducts/FeaturedProducts';
import EmptyFavorites from '../EmptyFavorites/EmptyFavorites';

import styles from './Products.module.css';

const OFFSET_PRODUCTS_COUNT = 24;

type InjectedProps = {|
  ...WithSelectedProductLinkInjectedProps,
  ...WithWindowTopIntersectionInjectedProps,
  +selectedCategoryId: string | null,
  +displaySocialBanner: boolean,
  +isLearnMoreVisible: boolean,
  +flags: {|
    socialEcomm: boolean,
  |},
|};

type StateProps = {|
  +offset: number,
  +offsetAllProducts: number,
  +displayedProductIds: Array<string>,
  +totalProductCount: number,
  +fetchingProducts: boolean,
  +productsRequestFailed: boolean,
  +viewSize: string,
  +topAccordionProduct: BundledProduct | null,
  +isCartEmpty: boolean,
  +selectedCategoryName: string,
  +hasFavorites: boolean,
  +selectedSort: Sort | null,
  +searchQuery: string,
  +searchResults: Array<string> | null,
  +showSearchBar: boolean,
  +haveAllInStockProductsRendered: boolean,
  +haveAllProductsRendered: boolean,
  +gwpProducts: Array<BundledProduct>,
  +productRecAbKey: string | null,
  +sortedProductIds: Array<string>,
  +sortedAllProductIds: Array<string>,
|};

type DispatchProps = {|
  +requestProducts: typeof requestProducts,
  +updateShowSearchBar: typeof updateShowSearchBar,
  +resetSearch: typeof resetSearch,
|};

type Props = {|
  ...InjectedProps,
  ...StateProps,
  ...DispatchProps,
|};

type ComponentState = {| scrolledUp: boolean, previousScrollY: number |};

class Products extends PureComponent<Props, ComponentState> {
  state = {
    scrolledUp: false,
    previousScrollY: window.scrollY,
  };

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll, false);
  }

  // this is used to request products on page load
  // some of first 24 products may be OOS in which case we need to request more products
  componentDidUpdate(prevProps) {
    const {
      offset,
      requestProducts,
      requestProductsById,
      selectedCategoryId,
      displayedProductIds,
      selectedSort,
      selectedProductLink,
      sortedProductIds,
      flags,
    } = this.props;
    const {
      rateAndReview: rateAndReviewFF,
      alternateSubscriptions: alternateSubscriptionsFF,
      publicSubscriptions: publicSubscriptionsFF,
    } = flags;
    const isRequestProductsNeeded = this.needToRequestProducts(); // we are not supporting search pagination for now
    if (
      isRequestProductsNeeded &&
      displayedProductIds.length > prevProps.displayedProductIds.length &&
      // selectedSort should never be null at this point, but Flow doesn't know that
      selectedSort !== null
    ) {
      if (!isPaginatedSortBy(selectedSort)) {
        const chunkedProductIds = chunk(
          sortedProductIds,
          OFFSET_PRODUCTS_COUNT
        );
        const index = Math.floor(offset / OFFSET_PRODUCTS_COUNT);
        requestProductsById(chunkedProductIds[index], false, rateAndReviewFF);
      } else {
        requestProducts({
          offset,
          selectedCategoryId,
          selectedSort,
          selectedProductLink,
          isAllProducts: false,
          rateAndReviewFF,
          alternateSubscriptionsFF,
          publicSubscriptionsFF,
        });
      }
    }
    this.handleOptimizelyTracking(prevProps);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll, false);
  }

  handleScroll = () => {
    const {
      offset,
      offsetAllProducts,
      selectedCategoryId,
      requestProducts,
      selectedSort,
      showSearchBar,
      updateShowSearchBar,
      viewSize,
      sortedProductIds,
      sortedAllProductIds,
      requestBestSellingProducts,
      requestProductsById,
      productRecAbKey,
      flags,
    } = this.props;
    const isRequestProductsNeeded = this.needToRequestProducts();
    const isRequestAllProductsNeeded = this.needToRequestAllProducts();
    const {
      rateAndReview: rateAndReviewFF,
      alternateSubscriptions: alternateSubscriptionsFF,
      publicSubscriptions: publicSubscriptionsFF,
    } = flags;
    if (
      isRequestProductsNeeded &&
      // selectedSort should never be null at this point, but Flow doesn't know that
      selectedSort !== null
    ) {
      //if selected sort is best selling or selected category is my favorites, make request using list of sorted products
      //use offset to calculate position in array
      if (
        !isPaginatedSortBy(selectedSort) ||
        selectedCategoryId === CATEGORY_ID.myfavorites
      ) {
        const chunkedProductIds = chunk(
          sortedProductIds,
          OFFSET_PRODUCTS_COUNT
        );
        const index = Math.floor(offset / OFFSET_PRODUCTS_COUNT);
        requestProductsById(chunkedProductIds[index], false, rateAndReviewFF);
      } else {
        requestProducts({
          offset,
          selectedCategoryId,
          selectedSort,
          rateAndReviewFF,
          alternateSubscriptionsFF,
          publicSubscriptionsFF,
        });
      }
    }
    // fetch all products for infinite scroll
    if (isRequestAllProductsNeeded && selectedSort) {
      if (!isPaginatedSortBy(selectedSort)) {
        if (!offsetAllProducts) {
          requestBestSellingProducts({
            selectedCategoryId: null,
            selectedSort,
            productRecAbKey,
            isAllProducts: true,
            rateAndReviewFF,
            alternateSubscriptionsFF,
            publicSubscriptionsFF,
          });
        } else {
          const chunkedProductIds = chunk(
            sortedAllProductIds,
            OFFSET_PRODUCTS_COUNT
          );
          const index = Math.floor(offsetAllProducts / OFFSET_PRODUCTS_COUNT);
          requestProductsById(chunkedProductIds[index], true, rateAndReviewFF);
        }
      } else {
        requestProducts({
          offset: offsetAllProducts,
          selectedCategoryId: null,
          selectedSort,
          isAllProducts: true,
          rateAndReviewFF,
          alternateSubscriptionsFF,
          publicSubscriptionsFF,
        });
      }
    }
    this.setState(this.setScrollState);
    if (showSearchBar && viewSize === 'xLarge') {
      updateShowSearchBar(false);
    }
  };

  setScrollState = (state: ComponentState) => {
    const { previousScrollY } = state;
    const currentScrollY = window.scrollY;
    let scrolledToBottom;
    if (document.body !== null) {
      scrolledToBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight;
    } else {
      scrolledToBottom = false;
    }
    return {
      previousScrollY: currentScrollY,
      scrolledUp: currentScrollY < previousScrollY && !scrolledToBottom,
    };
  };

  needToRequestProducts() {
    const {
      fetchingProducts,
      productsRequestFailed,
      selectedCategoryId,
      haveAllInStockProductsRendered,
      searchResults,
    } = this.props;
    const loadingEl = document.getElementById('loadingIcon');
    const rect = loadingEl && loadingEl.getBoundingClientRect();
    const windowHeight =
      window.innerHeight ||
      (document.documentElement && document.documentElement.clientHeight);
    const inView =
      rect && rect.top <= windowHeight + 450 && rect.top + rect.height >= 0;
    return (
      inView &&
      !searchResults &&
      !haveAllInStockProductsRendered &&
      !fetchingProducts &&
      !productsRequestFailed &&
      selectedCategoryId !== CATEGORY_ID.mycart
    );
  }

  needToRequestAllProducts() {
    const {
      fetchingProducts,
      productsRequestFailed,
      selectedCategoryId,
      haveAllProductsRendered,
      searchResults,
      offsetAllProducts,
    } = this.props;
    const loadingEl = document.getElementById('loadingIconAllProducts');
    const rect = loadingEl && loadingEl.getBoundingClientRect();
    const windowHeight =
      window.innerHeight ||
      (document.documentElement && document.documentElement.clientHeight);
    const inView =
      rect && rect.top <= windowHeight + 450 && rect.top + rect.height >= 0;
    return (
      inView &&
      !searchResults &&
      (!offsetAllProducts || !haveAllProductsRendered) &&
      !fetchingProducts &&
      !productsRequestFailed &&
      selectedCategoryId !== CATEGORY_ID.mycart
    );
  }

  handleOptimizelyTracking(prevProps) {
    const {
      selectedCategoryId,
      searchQuery,
      isEditPage,
      optimizelyFeaturedUIDecision,
      sortUpdated,
      userId,
      campaignId,
      isShopPage,
      userPlanCode,
    } = this.props;
    if (
      prevProps.optimizelyFeaturedUIDecision !== optimizelyFeaturedUIDecision
    ) {
      const featuredProductsExperimentRunning =
        (isEditPage || isShopPage) &&
        optimizelyFeaturedUIDecision.enabled &&
        !sortUpdated &&
        !searchQuery &&
        !selectedCategoryId;
      if (featuredProductsExperimentRunning) {
        trackOptimizelyExperiment(
          optimizelyFeaturedUIDecision,
          userId,
          campaignId,
          userPlanCode
        );
      }
    }
  }

  render() {
    const {
      offsetAllProducts,
      windowTopIntersection,
      forwardedRef,
      viewSize,
      topAccordionProduct,
      selectedCategoryId,
      selectedCategoryName,
      isCartEmpty,
      haveAllInStockProductsRendered,
      haveAllProductsRendered,
      searchQuery,
      searchResults,
      updateShowSearchBar,
      resetSearch,
      gwpProducts,
      fetchingSearchProducts,
      displaySocialBanner,
      teamCartProducts,
      flags,
      isLearnMoreVisible,
      isEditPage,
      optimizelyFeaturedUIDecision,
      sortUpdated,
      isShopPage,
      selectedSort,
      hasFavorites,
    } = this.props;
    const { scrolledUp } = this.state;
    const { socialEcomm: socialEcommFF } = flags;
    const scrollToTopButtonDisplayed = windowTopIntersection && scrolledUp;
    const resultSize = size(searchResults);
    const { enabled: featuredUIEnabled, variationName } =
      optimizelyFeaturedUIDecision;
    const isFavoritesTab = selectedCategoryId === CATEGORY_ID.myfavorites;
    const displayFeaturedProducts =
      (isEditPage || isShopPage) &&
      featuredUIEnabled &&
      (variationName === FEATURED_UI_DECISIONS.carousel ||
        variationName === FEATURED_UI_DECISIONS.pinnedProducts) &&
      !sortUpdated &&
      !searchQuery &&
      !isFavoritesTab;
    const displayFeaturedCarousel =
      displayFeaturedProducts &&
      variationName === FEATURED_UI_DECISIONS.carousel;
    const displayFeaturedGrid =
      displayFeaturedProducts &&
      variationName === FEATURED_UI_DECISIONS.pinnedProducts;
    const displayTopTrending =
      !selectedCategoryId &&
      selectedSort === 'BEST_SELLING' &&
      variationName === FEATURED_UI_DECISIONS.trendingProducts;
    const displayAllProducts =
      selectedCategoryId &&
      haveAllInStockProductsRendered &&
      !isShopPage &&
      !isStaticCategory(selectedCategoryId);

    return (
      <div
        //TO DO: update categories to all use the same header and styles
        className={classNames(styles.productsContainer, {
          [styles.favoritesTab]: isFavoritesTab,
        })}
        ref={forwardedRef}
      >
        <ErrorBoundary>
          <SponsoredWarningPopup />
        </ErrorBoundary>
        <GwpPopupsContainer />
        <SearchResults
          searchQuery={searchQuery}
          resultSize={resultSize}
          updateShowSearchBar={updateShowSearchBar}
          resetSearch={resetSearch}
          selectedCategoryId={selectedCategoryId}
        />
        {!searchQuery &&
          !isFavoritesTab &&
          (viewSize === VIEW_SIZE.small || viewSize === VIEW_SIZE.medium) && (
            <h1>{selectedCategoryName}</h1>
          )}
        {!searchQuery &&
          selectedCategoryId === CATEGORY_ID.mycart &&
          isCartEmpty && (
            <span className={styles.emptyCopy}>
              Once you add an item to your cart you can review all your
              selections here.
            </span>
          )}
        {topAccordionProduct !== null && (
          <div className="page-row">
            <ProductAccordion
              {...topAccordionProduct}
              teamCart={teamCartProducts[topAccordionProduct.id]}
              gwpProducts={gwpProducts}
              isLearnMoreVisible={isLearnMoreVisible}
            />
          </div>
        )}
        {!fetchingSearchProducts && (
          <div
            className={`${styles[selectedCategoryId] || ''} ${
              searchQuery ? styles.activeSearch : ''
            }`}
          >
            {displayFeaturedCarousel && (
              <FeaturedProducts viewSize={viewSize} />
            )}
            {displayFeaturedGrid && !selectedCategoryId && (
              <ProductGrid
                type="FEATURED"
                selectedCategoryId={selectedCategoryId}
                gwpProducts={gwpProducts}
                socialEcommFF={socialEcommFF}
              />
            )}
            {isFavoritesTab && !hasFavorites && <EmptyFavorites />}
            <ProductGrid
              type="IN_STOCK"
              selectedCategoryId={selectedCategoryId}
              gwpProducts={gwpProducts}
              socialEcommFF={socialEcommFF}
              displayTopTrending={displayTopTrending}
            />

            {/* sponsored and out-of-stock products should only render once all in-stock products have rendered */}
            <div className={styles.sponsored}>
              {haveAllInStockProductsRendered && (
                <ErrorBoundary>
                  <ProductGrid
                    type="GWP"
                    selectedCategoryId={selectedCategoryId}
                    gwpProducts={gwpProducts}
                    socialEcommFF={socialEcommFF}
                  />
                </ErrorBoundary>
              )}
            </div>
            <div className={styles.sponsored}>
              {haveAllInStockProductsRendered && !isFavoritesTab && (
                <ErrorBoundary>
                  <ProductGrid
                    type="SPONSORED"
                    selectedCategoryId={selectedCategoryId}
                    gwpProducts={gwpProducts}
                    socialEcommFF={socialEcommFF}
                  />
                </ErrorBoundary>
              )}
            </div>
            <div className={styles.soldOut}>
              {(haveAllInStockProductsRendered || searchResults) && (
                <ErrorBoundary>
                  <ProductGrid
                    type="SOLD_OUT"
                    selectedCategoryId={selectedCategoryId}
                    gwpProducts={gwpProducts}
                    socialEcommFF={socialEcommFF}
                  />
                </ErrorBoundary>
              )}
            </div>
          </div>
        )}
        {(fetchingSearchProducts ||
          (!searchResults && !haveAllInStockProductsRendered)) && (
          <LoadingSVG
            id="loadingIcon"
            className={styles.loadingIcon}
            alt="fetching products..."
          />
        )}
        {searchResults && (
          <div
            className={`${styles.shopAllButton} ${
              resultSize > 0 ? styles.results : ''
            }`}
          >
            <ShopAllButton
              resetSearch={resetSearch}
              selectedCategoryId={selectedCategoryId}
            />
          </div>
        )}
        {displayAllProducts && (
          <div className={styles.allProducts}>
            <h3 className={styles.title}>Shop All</h3>
            <ProductGrid
              type="ALL"
              selectedCategoryId={selectedCategoryId}
              gwpProducts={gwpProducts}
              socialEcommFF={socialEcommFF}
              displayTopTrending={displayTopTrending}
            />
            {!searchResults &&
              (!haveAllProductsRendered || !offsetAllProducts) && (
                <LoadingSVG
                  id="loadingIconAllProducts"
                  className={styles.loadingIcon}
                  alt="fetching products..."
                />
              )}
          </div>
        )}
        <ErrorBoundary>
          <div
            className={classNames(styles.scrollToTopButtonContainer, {
              [styles.displayed]: scrollToTopButtonDisplayed,
              [styles.socialBanner]: displaySocialBanner,
            })}
          >
            <ScrollToTopButton />
          </div>
        </ErrorBoundary>
      </div>
    );
  }
}

const selectGwpProducts = makeSelectGwpProducts();
function mapStateToProps(state: State, props: InjectedProps): StateProps {
  return {
    offset: state.productsState.displayedProductIds.length,
    offsetAllProducts: state.productsState.allProductIds.length,
    displayedProductIds: state.productsState.displayedProductIds,
    totalProductCount: state.productsState.totalProductCount,
    fetchingProducts: state.productsState.fetchingProducts,
    productsRequestFailed: state.productsState.productsRequestFailed,
    viewSize: state.viewState.viewSize,
    topAccordionProduct: getTopAccordionProduct(state, props),
    isCartEmpty: selectIsCartEmpty(state),
    selectedCategoryName: selectSelectedCategoryName(state, props),
    hasFavorites: selectHasFavorites(state),
    selectedSort: state.sortState.selectedSort,
    searchQuery: state.searchState.searchQuery,
    searchResults: state.searchState.searchResults,
    showSearchBar: state.searchState.showSearchBar,
    // these values should be equivalent when all in stock products have rendered. using >= just to be safe
    haveAllInStockProductsRendered:
      state.productsState.displayedProductIds.length >=
      state.productsState.totalProductCount,
    haveAllProductsRendered:
      state.productsState.allProductIds.length >=
      state.productsState.allProductCount,
    gwpProducts: selectGwpProducts(state),
    sortedProductIds: state.productsState.sortedProductIds,
    sortedAllProductIds: state.productsState.sortedAllProductIds,
    fetchingSearchProducts: state.productsState.fetchingSearchProducts,
    teamCartProducts: selectTeamCartProducts(state),
    sortUpdated: state.sortState.sortUpdated,
    userId: state.userState.userId,
    userPlanCode: state.userState.planCode,
    campaignId: state.campaignState.campaignId,
    productRecAbKey: state.sortState.productRecAbKey,
  };
}

export default compose(
  withWindowTopIntersection,
  withSelectedProductLink,
  withSelectedCategoryId,
  withDisableScrollRestoration,
  withLDConsumer(),
  withOptimizelyFF,
  connect<
    {| ...StateProps, ...DispatchProps, ...InjectedProps |},
    InjectedProps,
    _,
    _,
    _,
    _
  >(mapStateToProps, {
    requestProducts,
    updateShowSearchBar,
    resetSearch,
    requestProductsById,
    requestBestSellingProducts,
  })
)(Products);
